import { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import SiteHeader from '@/components/feature/SiteHeader';
import SiteFooter from '@/components/feature/SiteFooter';
import BackToTop from '@/components/feature/BackToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ArticleCard from '@/components/feature/ArticleCard';
import { useSeo } from '@/hooks/useSeo';
import { articles } from '@/mocks/articles';
import { categories } from '@/mocks/categories';
import NotFound from '@/pages/NotFound';

type SortKey = 'heat' | 'latest';

export default function CategoryPage() {
  const { slug = '' } = useParams();
  const category = categories.find((c) => c.slug === slug);
  const [sortKey, setSortKey] = useState<SortKey>('heat');
  const [tag, setTag] = useState<string>('全部');
  const [keyword, setKeyword] = useState('');

  const categoryArticles = useMemo(
    () => articles.filter((item) => item.categorySlug === slug),
    [slug],
  );

  const tagOptions = useMemo(() => {
    const set = new Set<string>();
    categoryArticles.forEach((item) => item.tags.forEach((t) => set.add(t)));
    return ['全部', ...Array.from(set)];
  }, [categoryArticles]);

  const visibleArticles = useMemo(() => {
    const list = categoryArticles.filter((item) => {
      const matchTag = tag === '全部' || item.tags.includes(tag);
      const kw = keyword.trim();
      const matchKeyword =
        !kw || item.title.includes(kw) || item.summary.includes(kw) || item.tags.some((t) => t.includes(kw));
      return matchTag && matchKeyword;
    });
    return list.sort((a, b) =>
      sortKey === 'heat'
        ? b.heat - a.heat
        : new Date(b.publishedISO).getTime() - new Date(a.publishedISO).getTime(),
    );
  }, [categoryArticles, sortKey, tag, keyword]);

  const otherCategories = categories.filter((c) => c.slug !== slug);

  const jsonLd = useMemo(() => {
    if (!category) return undefined;
    const origin = typeof window !== 'undefined' ? window.location.origin : '';
    const basePath = typeof __BASE_PATH__ !== 'undefined' ? __BASE_PATH__.replace(/\/$/, '') : '';
    const siteUrl = `${origin}${basePath}`;
    return [
      {
        '@context': 'https://schema.org',
        '@type': 'CollectionPage',
        name: `${category.name} - 17c吃瓜`,
        description: category.longDescription,
        url: `${siteUrl}/category/${category.slug}`,
        inLanguage: 'zh-CN',
      },
      {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: '首页', item: `${siteUrl}/` },
          { '@type': 'ListItem', position: 2, name: '主题分类', item: `${siteUrl}/#topics` },
          { '@type': 'ListItem', position: 3, name: category.name },
        ],
      },
      {
        '@context': 'https://schema.org',
        '@type': 'ItemList',
        name: `${category.name}事件列表`,
        itemListElement: categoryArticles.map((item, index) => ({
          '@type': 'ListItem',
          position: index + 1,
          name: item.title,
          url: `${siteUrl}/article/${item.slug}`,
        })),
      },
    ];
  }, [category, categoryArticles]);

  useSeo({
    title: category
      ? `${category.name} - 最新爆料与全网热议 | 17c吃瓜`
      : '主题分类 - 17c吃瓜',
    description: category
      ? `${category.description}17c吃瓜${category.name}栏目实时更新今日热门事件，梳理${category.keywords.join('、')}等话题的关键细节与网友焦点。`
      : '17c吃瓜主题分类页',
    keywords: category ? [category.name, ...category.keywords, '今日热门事件', '17c吃瓜'] : ['17c吃瓜'],
    path: `/category/${slug}`,
    jsonLd,
  });

  if (!category) {
    return <NotFound />;
  }

  return (
    <div className="flex min-h-screen flex-col bg-background-50">
      <SiteHeader />
      <main className="flex-1 w-full">
        <div className="w-full px-4 md:px-8 pt-6">
          <Breadcrumb
            items={[
              { label: '首页', to: '/' },
              { label: '主题分类', to: '/#topics' },
              { label: category.name },
            ]}
          />
        </div>

        <section className="w-full px-4 md:px-8 py-6 md:py-8">
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-6 lg:gap-8 items-center">
            <div>
              <span className="inline-flex items-center gap-2 text-xs text-primary-700 bg-primary-50 border border-primary-200 px-2.5 py-1 rounded-full">
                <i className={category.icon}></i>
                {category.count} 篇内容 · 更新于 {category.updatedAt}
              </span>
              <h1 className="mt-3 font-heading text-3xl md:text-5xl font-black text-foreground-950 leading-tight">
                {category.name}
              </h1>
              <p className="mt-4 text-sm md:text-base text-foreground-600 leading-relaxed max-w-2xl">
                {category.longDescription}
              </p>
              <div className="mt-4 flex flex-wrap gap-2">
                {category.keywords.map((kw) => (
                  <span
                    key={kw}
                    className="px-3 py-1.5 text-xs rounded-full bg-secondary-50 text-secondary-900 border border-secondary-200"
                  >
                    {kw}
                  </span>
                ))}
              </div>
            </div>
            <div className="w-full h-48 md:h-56 rounded-lg overflow-hidden border border-background-200">
              <img
                src={category.cover}
                alt={`${category.name}栏目专题图 · 17c吃瓜`}
                title={`${category.name} · ${category.description}`}
                className="w-full h-full object-cover object-top"
              />
            </div>
          </div>
        </section>

        <section className="w-full px-4 md:px-8 pb-4">
          <div className="rounded-lg border border-background-200 bg-background-100 p-3 md:p-4 flex flex-col lg:flex-row lg:items-center gap-3">
            <div className="flex items-center gap-1 bg-background-50 border border-background-200 rounded-full p-1 shrink-0">
              {(
                [
                  { key: 'heat', label: '按热度' },
                  { key: 'latest', label: '按最新' },
                ] as { key: SortKey; label: string }[]
              ).map((option) => (
                <button
                  key={option.key}
                  type="button"
                  onClick={() => setSortKey(option.key)}
                  className={`px-3.5 py-1.5 text-xs rounded-full transition-colors cursor-pointer whitespace-nowrap ${
                    sortKey === option.key
                      ? 'bg-primary-500 text-background-50'
                      : 'text-foreground-600 hover:text-primary-600'
                  }`}
                >
                  {option.label}
                </button>
              ))}
            </div>

            <div className="flex-1 flex items-center gap-2 bg-background-50 border border-background-200 rounded-md px-3">
              <i className="ri-search-line text-foreground-500 text-sm"></i>
              <input
                type="search"
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                placeholder={`在「${category.name}」中筛选事件`}
                aria-label={`筛选${category.name}事件`}
                className="flex-1 h-10 bg-transparent text-sm text-foreground-900 placeholder:text-foreground-500 focus:outline-none"
              />
            </div>

            <div className="flex flex-wrap gap-1.5">
              {tagOptions.map((option) => (
                <button
                  key={option}
                  type="button"
                  onClick={() => setTag(option)}
                  className={`px-3 py-1.5 text-xs rounded-full border transition-colors cursor-pointer whitespace-nowrap ${
                    tag === option
                      ? 'bg-accent-500 text-accent-950 border-accent-500'
                      : 'bg-background-50 text-foreground-600 border-background-300 hover:border-accent-300'
                  }`}
                >
                  {option}
                </button>
              ))}
            </div>
          </div>
          <p className="mt-2.5 text-xs text-foreground-500">
            共找到 {visibleArticles.length} 条相关事件
          </p>
        </section>

        <section className="w-full px-4 md:px-8 pb-12 md:pb-16">
          {visibleArticles.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {visibleArticles.map((item) => (
                <ArticleCard key={item.slug} article={item} />
              ))}
            </div>
          ) : (
            <div className="rounded-lg border border-dashed border-background-300 bg-background-100 py-16 text-center">
              <i className="ri-inbox-line text-3xl text-foreground-400"></i>
              <p className="mt-3 text-sm text-foreground-600">这个筛选条件下还没有内容，换个关键词试试。</p>
              <button
                type="button"
                onClick={() => {
                  setKeyword('');
                  setTag('全部');
                }}
                className="mt-4 px-4 py-2 text-sm rounded-md bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                重置筛选
              </button>
            </div>
          )}
        </section>

        <section className="w-full bg-background-100 py-10 md:py-12">
          <div className="w-full px-4 md:px-8">
            <h2 className="font-heading text-lg md:text-xl font-bold text-foreground-950 mb-4">
              继续浏览其他栏目
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
              {otherCategories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className="group flex items-center gap-3 rounded-lg border border-background-200 bg-background-50 px-4 py-3.5 hover:border-primary-300 transition-colors cursor-pointer"
                >
                  <span className="w-9 h-9 flex items-center justify-center rounded-md bg-primary-50 text-primary-600 shrink-0">
                    <i className={cat.icon}></i>
                  </span>
                  <span className="min-w-0">
                    <span className="block text-sm text-foreground-900 group-hover:text-primary-600 transition-colors">
                      {cat.name}
                    </span>
                    <span className="block text-[11px] text-foreground-500 truncate">{cat.description}</span>
                  </span>
                </Link>
              ))}
            </div>
          </div>
        </section>
      </main>
      <SiteFooter />
      <BackToTop />
    </div>
  );
}